import { useState, useEffect, useRef } from 'react'
const BASE_URL = process.env.REACT_APP_BASE_URL;
const EditLessonResourceModal = ({
  resourceId,
  setEditLessonModal,
  fetchCourseContent,
}) => {
  const [fileName, setFileName] = useState('')
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [updateLoading, setUpdateLoading] = useState(false)
  const fileInputRef = useRef(null)
  // Fetch existing resource
  useEffect(() => {
    const fetchResource = async () => {
      try {
        setLoading(true)
        const response = await fetch(`${BASE_URL}/courses/${resourceId}/lesson/get/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}`, "x-api-key": process.env.REACT_APP_X_API_KEY },
        })
        if (!response.ok) throw new Error('Failed to fetch resource')
        const data = await response.json()
        setFileName(data.file || '')
      } catch (error) {
        console.error('Failed to fetch resource', error)
        setFileName('')
      } finally {
        setLoading(false)
      }
    }
    fetchResource()
  }, [resourceId])
  // Handle drag & drop
  const handleDrop = (e) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0])
      setFileName(e.dataTransfer.files[0].name)
      e.dataTransfer.clearData()
    }
  }
  const handleDragOver = (e) => e.preventDefault()
  const handleSave = async () => {
    setUpdateLoading(true)
    try {
      const formData = new FormData()
      if (file) {
        formData.append('file', file)
      } else {
        formData.append('file', fileName) // keep URL if no file
      }
      const response = await fetch(`${BASE_URL}/courses/${resourceId}/lesson/update/`, {
        method: 'PUT',
        body: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}`, "x-api-key": process.env.REACT_APP_X_API_KEY }
      })

      if (!response.ok) throw new Error('Failed to save resource')

      fetchCourseContent()
      setEditLessonModal(false)
      setUpdateLoading(false)

      // Show success alert
      alert('Lesson resource updated successfully!')
    } catch (error) {
      console.error('Failed to save resource', error)
      alert('Failed to update lesson resource. Please try again.')
      setUpdateLoading(false)
    }
  }
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={() => setEditLessonModal(false)}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Edit Resource</h2>
              <p className="text-sm text-gray-500 mt-0.5">Update your lesson resource</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="w-8 h-8 border-2 border-[#0f6abf] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        ) : (
          <>
            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {fileName && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Current Resource</p>
                    <a
                      href={fileName}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-[#0f6abf] hover:underline"
                    >
                      {fileName}
                    </a>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
                  <label className="text-sm font-semibold text-gray-700">
                    Upload New File
                  </label>
                </div>

                <div
                  className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-[#0f6abf] hover:bg-[#e8f0f7]/10 transition-colors"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current.click()}
                >
                  <div className="w-12 h-12 bg-[#e8f0f7] rounded-lg flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    {file ? file.name : 'Drag & drop a file here'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {file ? '' : 'or click to select'}
                  </p>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      setFile(e.target.files[0])
                      setFileName(e.target.files[0].name)
                    }
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
              <div className="flex items-center justify-end gap-3">
                <button
                  className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
                  onClick={() => setEditLessonModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2.5 bg-[#0f6abf] text-white font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors flex items-center gap-2 disabled:opacity-50"
                  onClick={handleSave}
                  disabled={updateLoading}
                >
                  {updateLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>

  )
}

export default EditLessonResourceModal
